import { types } from "mobx-state-tree";
import {
  fetchAuthSession,
  signIn,
  signOut,
  getCurrentUser,
  fetchUserAttributes,
  confirmSignIn,
  fetchMFAPreference,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { generateClient } from "aws-amplify/api";
import { customGetUser } from "../graphql/customQueries";
import { CustomGetUserQuery } from "../API";
import { getAccessTokenForLambdaAuth } from "../utils/authHelpers";
(window as any).LOG_LEVEL = "INFO";
type UserData = {
  attributes: {
    email: string;
  };
  username: string;
};
const amplifyClient = generateClient();
export const AuthStore = types
  .model({
    amplifyListenerIsOn: types.optional(types.boolean, false),
    authState: types.maybe(types.string),
    user: types.maybe(types.frozen()),
    userEmail: types.optional(types.string, ""),
    userName: types.maybe(types.string),
    isSignedIn: types.optional(types.boolean, false),
    lastSignInErrorMessage: types.maybe(types.string),
    userCreds: types.maybe(types.frozen()),
    newSession: types.optional(types.boolean, false),
    specialAuthState: types.maybeNull(types.string),
    isSigninInProgress: types.optional(types.boolean, false),
    // Account data from DynamoDB
    accountIds: types.optional(types.array(types.string), []),
    primaryAccountId: types.maybe(types.string),
    accountsLoaded: types.optional(types.boolean, false),
  })
  .views((self) => ({
    async getGroupIds() {
      // Return account IDs from DynamoDB instead of cognito:groups
      if (self.accountIds.length > 0) {
        return self.accountIds.slice();
      }
      // Fallback to cognito:groups if accounts haven't been loaded yet
      const { tokens: session } = await fetchAuthSession();
      return session?.idToken?.payload["cognito:groups"] as string[];
    },
    get primaryAccount() {
      return self.primaryAccountId;
    },
  }))
  .actions((self) => ({
    setIsSIgninInProgress(flag: boolean) {
      self.isSigninInProgress = flag;
    },
    setAccountIds(accountIds: string[]) {
      self.accountIds.replace(accountIds);
    },
    setPrimaryAccountId(accountId: string) {
      self.primaryAccountId = accountId;
    },
    setAccountsLoaded(flag: boolean) {
      self.accountsLoaded = flag;
    },
    async hasBlingsAccount() {
      const groups = await self.getGroupIds();
      return groups?.includes("blings_account");
    },
    // Helper function to fetch user and accounts from DynamoDB (with pagination)
    // This is called by both oneOfAccountsHasTotpRequired and loadAccountData
    async fetchUserAccountsFromDB() {
      try {
        // Get Amplify user ID
        const amplifyUser = await getCurrentUser();

        // Fetch user and accounts from DynamoDB (with pagination)
        const allAccountUsers: Array<{
          id: string;
          accountId: string;
          account: {
            id: string;
            gid: string;
            name: string;
            mfaEnabled?: boolean | null;
          } | null;
        }> = [];
        let nextToken: string | null = null;

        const authToken = await getAccessTokenForLambdaAuth();

        do {
          const response: { data: CustomGetUserQuery } =
            await amplifyClient.graphql({
              query: customGetUser,
              variables: {
                id: amplifyUser.userId,
                accountsLimit: 100,
                ...(nextToken && { accountsNextToken: nextToken }),
              },
              authMode: "lambda",
              authToken,
            });

          const userData = response.data;

          if (!userData?.getUser) {
            throw new Error("User not found in database");
          }

          const accountUsers = userData?.getUser?.accounts?.items || [];
          accountUsers.forEach((item) => {
            if (item && item.account && item.account.tierId === "ENTERPRISE") {
              allAccountUsers.push({
                id: item.id,
                accountId: item.accountId,
                account: item.account,
              });
            }
          });

          nextToken = userData?.getUser?.accounts?.nextToken || null;
        } while (nextToken);

        return allAccountUsers;
      } catch (error) {
        console.error("Error fetching user accounts from database:", error);
        throw error;
      }
    },
    async oneOfAccountsHasTotpRequired() {
      try {
        // If accounts are already loaded, use cached data
        if (self.accountsLoaded) {
          // This shouldn't happen, but if it does, we can skip fetching
          console.log("Accounts already loaded, checking cached MFA status");
        }

        // Fetch accounts and check MFA settings
        const allAccountUsers = await this.fetchUserAccountsFromDB();

        // Store the account data for later use (avoid duplicate fetch in loadAccountData)
        if (allAccountUsers.length > 0) {
          const allAccountIds = allAccountUsers.map((au) => au.accountId);
          this.setAccountIds(allAccountIds);
          this.setPrimaryAccountId(allAccountIds[0]);
          this.setAccountsLoaded(true);
          console.log("Account data cached during MFA check:", allAccountIds);
        }

        // Check if any account has MFA enabled
        const hasTotpRequired = allAccountUsers.some(
          (accountUser) => accountUser.account?.mfaEnabled === true
        );

        return hasTotpRequired;
      } catch (error) {
        console.error("Error checking account MFA status:", error);
        // Return false on error to allow sign-in to continue
        return false;
      }
    },
    setSpecialAuthState(state: string | null) {
      self.specialAuthState = state;
    },
    setUser(user: UserData, email: string) {
      self.userEmail = email;
      self.userName = user.username;
    },
    setNewSession(flag: boolean) {
      self.newSession = flag;
    },
    setUserCreds(creds: { user: any; username: string }) {
      self.userCreds = { ...creds };
    },
    setAuthState(authState: string | undefined) {
      self.authState = authState;
    },
    setIsSignedIn(flag: boolean) {
      self.isSignedIn = flag;
    },
    setLastSignInErrorMessage(message: string | undefined) {
      self.lastSignInErrorMessage = message;
    },
    setSignInState(
      signIn: boolean,
      { error, user }: { error?: string; user?: any }
    ) {
      if (!signIn) {
        self.isSignedIn = false;
        if (typeof error !== undefined) {
          self.lastSignInErrorMessage = error;
        }
        this.setIsSIgninInProgress(false);
      } else {
        if (user) {
          // fetch attributes and apply
          let email;
          fetchUserAttributes().then(async (attributes) => {
            const mfaPreference = await fetchMFAPreference();
            const oneOfAccountsHasTotpRequired =
              await this.oneOfAccountsHasTotpRequired();
            if (
              !mfaPreference.enabled &&
              (attributes?.["custom:mfaConfig"] === "2" ||
                ((attributes?.["custom:mfaConfig"] === "0" ||
                  attributes?.["custom:mfaConfig"] == null) &&
                  oneOfAccountsHasTotpRequired))
            ) {
              await signOut();
              this.setLastSignInErrorMessage(
                "Setup TOTP in Blings Platform or Studio"
              );
            } else {
              this.setIsSignedIn(true);
              this.setSpecialAuthState(null);
              this.setLastSignInErrorMessage(undefined);
              email = attributes.email;
              this.setUser(user, email || "");
            }
            this.setIsSIgninInProgress(false);
          });
        }
      }
    },
    setLastSignInError(error: string | undefined) {
      self.lastSignInErrorMessage = error;
    },
  }))
  .actions((self) => ({
    startListeningToAmplifyAuth() {
      getCurrentUser()
        .then((user) => {
          if (user) {
            self.setSignInState(true, { user });
          }
        })
        .catch((err) => {
          console.log("User not authenticated");
          console.log("error: ", err);
        });
      if (self.amplifyListenerIsOn) {
        return;
      }
      Hub.listen("auth", (data) => {
        const { event } = data.payload;
        self.setAuthState(event);
        if (event.toLowerCase() === "signin") {
          self.setSignInState(true, {});
        }
      });
    },
    async handleTotpCodeOrFirstLogin(code: string) {
      try {
        self.setIsSIgninInProgress(true);
        const user = await confirmSignIn({ challengeResponse: code });
        self.setLastSignInErrorMessage(undefined);
        self.setSignInState(true, { user });
      } catch (error) {
        self.setIsSIgninInProgress(false);
        const { message } = error as any;
        console.log("error signing in", error);
        self.setSignInState(false, { error: message });
      }
    },
    async signIn(username: string, password: string | undefined) {
      if (!username || !password) {
        return;
      }
      try {
        self.setIsSIgninInProgress(true);
        const user = await signIn({
          username,
          password,
        });
        if (user.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
          self.setSpecialAuthState("totp");
          self.setSignInState(false, { user });
        } else if (
          user.nextStep.signInStep ===
          "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
        ) {
          self.setSpecialAuthState("newPassword");
          self.setSignInState(false, { user });
        } else {
          self.setSignInState(true, { user });
        }
        self.setIsSIgninInProgress(false);
      } catch (error) {
        self.setIsSIgninInProgress(false);
        const { message } = error as any;
        console.log("error signing in", error);
        self.setSignInState(false, { error: message });
      }
    },

    async signOut() {
      await signOut();
      window.location.reload();
      self.setSignInState(false, { error: "" });
    },
    async loadAccountData() {
      try {
        // If accounts are already loaded (from MFA check), skip fetching
        if (self.accountsLoaded && self.accountIds.length > 0) {
          console.log("Account data already loaded, skipping fetch");
          return;
        }

        // Fetch user and accounts from DynamoDB
        const allAccountUsers = await self.fetchUserAccountsFromDB();

        // Set account IDs in the store
        if (allAccountUsers.length === 0) {
          console.warn("No accounts found for user");
          return;
        }

        if (allAccountUsers.length > 1) {
          console.log("User has multiple accounts:", allAccountUsers.length);
        }

        // Extract account IDs
        const allAccountIds = allAccountUsers.map((au) => au.accountId);

        // Set account IDs based on accounts from DynamoDB
        self.setAccountIds(allAccountIds);
        self.setPrimaryAccountId(allAccountIds[0]);
        self.setAccountsLoaded(true);

        console.log("Account data loaded successfully:", allAccountIds);
      } catch (e) {
        console.error("Error loading account data:", e);
        self.setAccountsLoaded(false);
      }
    },
  }));
